import { createSignal, onCleanup, onMount } from "solid-js";

/** Pixels the column gives up, or takes back, per arrow key on the grip. */
const PANE_RESIZE_STEP_PX = 16;

export type SidePaneResizeElements = {
	/**
	 * Holds the column and the pane side by side. Sets, in pixels,
	 * `--side-pane-column-min-width` (narrowest `content` may be) and
	 * `--side-pane-min-width` (narrowest the pane may be).
	 */
	row: HTMLElement;
	/** What the pane sits beside, gutters and all. */
	column: HTMLElement;
	/** The part of the column the width is chosen for: the column itself where it has no gutters. */
	content: HTMLElement;
};

export type SidePaneResize = ReturnType<typeof useSidePaneResize>;

/**
 * Lets a side pane be widened by dragging its left edge. The pane takes
 * whatever width the column leaves, so dragging sets the column's width and the
 * host lays the pane out from {@link columnWidth} and {@link overlap}.
 * Undefined leaves the width to the stylesheet, which is where it starts and
 * where a double-click on the grip puts it back.
 */
export function useSidePaneResize(
	elements: () => SidePaneResizeElements | undefined,
) {
	const [columnWidth, setColumnWidth] = createSignal<number>();
	/** What the pane takes beyond that, lying over the column rather than beside it. */
	const [overlap, setOverlap] = createSignal(0);
	const [isResizing, setIsResizing] = createSignal(false);
	/** Where the grip sits across the row, as ARIA reports a splitter. */
	const [separatorPosition, setSeparatorPosition] = createSignal(0);

	/**
	 * How far the column may be pulled: no narrower than its own minimum, and
	 * no wider than leaves the pane the width it needs. The gutters are
	 * measured rather than assumed, since they are the column's padding rather
	 * than anything its content knows about.
	 */
	const bounds = () => {
		const els = elements();
		if (els === undefined) {
			return undefined;
		}
		const styles = getComputedStyle(els.row);
		const row = els.row.getBoundingClientRect().width;
		const smallest = Number.parseFloat(
			styles.getPropertyValue("--side-pane-column-min-width"),
		);
		const paneSmallest = Number.parseFloat(
			styles.getPropertyValue("--side-pane-min-width"),
		);
		const gutters =
			els.column.getBoundingClientRect().width -
			els.content.getBoundingClientRect().width;
		return {
			row,
			gutters,
			smallest,
			largest: Math.max(smallest, row - paneSmallest - gutters),
		};
	};

	/**
	 * Takes the width the drag asks the column for, which may be less than the
	 * column can give. A column already at its narrowest stays there and the
	 * rest of the pull carries the pane over the top of it, as far as the row's
	 * own edge; the column keeps its width and its wrapping throughout.
	 */
	const resizeTo = (askedFor: number) => {
		const b = bounds();
		if (b === undefined) {
			return;
		}
		const asked = Math.min(askedFor, b.largest);
		const width = Math.max(asked, b.smallest);
		const over = Math.min(
			Math.max(b.smallest - asked, 0),
			b.smallest + b.gutters,
		);
		setColumnWidth(width);
		setOverlap(over);
		setSeparatorPosition(
			Math.round(((width + b.gutters - over) / b.row) * 100),
		);
	};

	/** Reads the position back off the layout, for widths nobody has chosen. */
	const sync = () => {
		const b = bounds();
		const els = elements();
		if (b === undefined || els === undefined) {
			return;
		}
		const column = els.column.getBoundingClientRect().width;
		setSeparatorPosition(Math.round(((column - overlap()) / b.row) * 100));
	};

	/** What the grip is holding: the content's width, less any overlap. */
	const draggedWidth = () =>
		(elements()?.content.getBoundingClientRect().width ?? 0) - overlap();

	let endDrag: (() => void) | undefined;

	const start = (event: PointerEvent & { currentTarget: HTMLElement }) => {
		if (event.button !== 0 || elements() === undefined) {
			return;
		}
		event.preventDefault();
		endDrag?.();
		const { pointerId } = event;
		const startX = event.clientX;
		const startWidth = draggedWidth();
		event.currentTarget.setPointerCapture(pointerId);
		setIsResizing(true);
		// The pointer spends the drag over the column and the pane either side
		// of the edge, which would otherwise select text as it passes.
		document.body.style.cursor = "col-resize";
		document.body.style.userSelect = "none";

		// Capture doesn't promise the release comes back to the grip: let go
		// over an embedded frame or outside the window and it can go elsewhere
		// or nowhere. So the drag listens on the window, and a move with the
		// button already up means the release was missed.
		const onMove = (move: PointerEvent) => {
			if (move.pointerId !== pointerId) {
				return;
			}
			if ((move.buttons & 1) === 0) {
				end();
				return;
			}
			resizeTo(startWidth + move.clientX - startX);
		};
		const onRelease = (release: PointerEvent) => {
			if (release.pointerId === pointerId) {
				end();
			}
		};
		const end = () => {
			endDrag = undefined;
			setIsResizing(false);
			document.body.style.cursor = "";
			document.body.style.userSelect = "";
			window.removeEventListener("pointermove", onMove);
			window.removeEventListener("pointerup", onRelease);
			window.removeEventListener("pointercancel", onRelease);
			window.removeEventListener("blur", end);
		};
		endDrag = end;

		window.addEventListener("pointermove", onMove);
		window.addEventListener("pointerup", onRelease);
		window.addEventListener("pointercancel", onRelease);
		window.addEventListener("blur", end);
	};

	const nudge = (event: KeyboardEvent) => {
		const steps: Record<string, number> = {
			ArrowLeft: -PANE_RESIZE_STEP_PX,
			ArrowRight: PANE_RESIZE_STEP_PX,
		};
		const step = steps[event.key];
		if (step === undefined || elements() === undefined) {
			return;
		}
		event.preventDefault();
		resizeTo(draggedWidth() + step);
	};

	const reset = () => {
		setColumnWidth(undefined);
		setOverlap(0);
		requestAnimationFrame(sync);
	};

	onMount(() => {
		// A window that changes size under a chosen width can leave the pane no
		// room; the choice is kept, within what the new row can hold.
		const onWindowResize = () => {
			if (columnWidth() === undefined) {
				sync();
				return;
			}
			resizeTo(draggedWidth());
		};
		window.addEventListener("resize", onWindowResize);
		onCleanup(() => {
			window.removeEventListener("resize", onWindowResize);
			endDrag?.();
		});
	});

	return {
		columnWidth,
		overlap,
		isResizing,
		separatorPosition,
		start,
		nudge,
		reset,
		sync,
	};
}
